/**
 * PaddlePaywallProductDialog Component
 *
 * Dialog for creating and editing Paddle paywall products.
 * Allows selecting Paddle environment, products and prices.
 * Environment is selected per-product (not globally).
 *
 * @module paywall-products/components/PaddlePaywallProductDialog
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  Chip,
  Stack,
} from '@mui/material';
import { PAYWALL_PRODUCTS_TEST_IDS } from '@modules/paywall-products/testIds';
import CloseIcon from '@mui/icons-material/Close';
import ScienceIcon from '@mui/icons-material/Science';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { usePaddleConnect } from '@modules/paddle-connect';
import { defaultPaddleEnvironment } from '@modules/paywall-products/utils/defaultPaddleEnvironment';
import { formatPaddlePrice } from '@modules/shared-locales/paddle-locales';
import type { PaywallProduct } from '@segmently/schema-models/payments';
import type { PaddleProduct, PaddlePrice } from '@modules/paddle-connect';

/**
 * Props for PaddlePaywallProductDialog
 */
export interface PaddlePaywallProductDialogProps {
  /** Whether dialog is open */
  open: boolean;

  /** Product to edit (null for create mode) */
  product: PaywallProduct | null;

  /** Project ID */
  projectId: string;

  /** Initial environment from the picker; never overrides an edited product. */
  defaultEnvironment?: 'sandbox' | 'production';

  /** Close handler */
  onClose: () => void;

  /** Save handler */
  onSave: (data: Omit<PaywallProduct, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
}

/**
 * Form state for Paddle product dialog
 */
interface PaddleProductFormState {
  name: string;
  description: string;
  type: 'one_time' | 'subscription';
  paddleEnvironment: 'sandbox' | 'production';
  paddleProductId: string;
  paddlePriceId: string;
  /** For subscription: billing interval */
  interval: 'day' | 'week' | 'month' | 'year';
  intervalCount: number;
  /** Checkout mode: embedded for inline, redirect for overlay */
  checkoutMode: 'embedded' | 'redirect';
}

const DEFAULT_FORM_STATE: PaddleProductFormState = {
  name: '',
  description: '',
  type: 'subscription',
  paddleEnvironment: 'sandbox', // Default to sandbox for safety
  paddleProductId: '',
  paddlePriceId: '',
  interval: 'month',
  intervalCount: 1,
  checkoutMode: 'redirect',
};

/**
 * PaddlePaywallProductDialog Component
 *
 * Creates or edits a PaywallProduct linked to a Paddle product/price.
 * Environment is selected at the top and determines which Paddle
 * products/prices are available for selection.
 */
export function PaddlePaywallProductDialog({
  open,
  product,
  projectId,
  defaultEnvironment,
  onClose,
  onSave,
}: PaddlePaywallProductDialogProps) {
  const { status, listProducts, listPrices, loading: connectionLoading } = usePaddleConnect(projectId);

  const [form, setForm] = useState<PaddleProductFormState>(DEFAULT_FORM_STATE);
  const [paddleProducts, setPaddleProducts] = useState<PaddleProduct[]>([]);
  const [paddlePrices, setPaddlePrices] = useState<PaddlePrice[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingPrices, setLoadingPrices] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const environmentDefaultApplied = useRef(false);

  // Get environment connection status
  const sandboxConnected = status?.sandbox?.connected ?? false;
  const productionConnected = status?.production?.connected ?? false;

  // Reset only for a new dialog session or edited product, never for a status refresh.
  useEffect(() => {
    environmentDefaultApplied.current = false;
    if (product && product.paymentProvider === 'paddle') {
      setForm({
        name: product.name,
        description: product.description || '',
        type: product.type,
        paddleEnvironment: product.paddleEnvironment || 'sandbox',
        paddleProductId: product.paddleProductId || '',
        paddlePriceId: product.paddlePriceId || '',
        interval: product.subscription?.interval || 'month',
        intervalCount: product.subscription?.intervalCount || 1,
        checkoutMode: product.checkoutMode || 'redirect',
      });
    } else if (!product) {
      setForm({ ...DEFAULT_FORM_STATE, paddleEnvironment: defaultEnvironment ?? DEFAULT_FORM_STATE.paddleEnvironment });
    }
    setError(null);
  }, [product, open, projectId, defaultEnvironment]);

  // Apply after the reset and first successful status read. Later observations must
  // not overwrite the owner's environment choice or an existing product binding.
  const activeEnvironment = status?.activeEnvironment;
  const hasStatus = status !== null;
  useEffect(() => {
    if (!open || product || !hasStatus || connectionLoading || environmentDefaultApplied.current) return;
    environmentDefaultApplied.current = true;
    const paddleEnvironment = defaultEnvironment ?? defaultPaddleEnvironment({ sandboxConnected, productionConnected, activeEnvironment });
    setForm(prev => ({ ...prev, paddleEnvironment, paddleProductId: '', paddlePriceId: '' }));
  }, [open, product, projectId, hasStatus, connectionLoading, sandboxConnected, productionConnected, activeEnvironment, defaultEnvironment]);

  // Load Paddle products when environment changes
  useEffect(() => {
    let current = true;
    if (open && form.paddleEnvironment) {
      const isEnvConnected = form.paddleEnvironment === 'sandbox' ? sandboxConnected : productionConnected;
      if (isEnvConnected) {
        setLoadingProducts(true);
        setPaddleProducts([]);
        setError(null);
        listProducts(form.paddleEnvironment)
          .then((products) => {
            if (current) setPaddleProducts(products);
          })
          .catch((err) => {
            if (!current) return;
            console.error('Failed to load Paddle products:', err);
            setError('Failed to load Paddle products. Check your connection.');
            setPaddleProducts([]);
          })
          .finally(() => { if (current) setLoadingProducts(false); });
      } else {
        setPaddleProducts([]);
        setLoadingProducts(false);
      }
    }
    return () => { current = false; };
  }, [open, projectId, form.paddleEnvironment, sandboxConnected, productionConnected, listProducts, product]);

  // Load prices when product is selected
  useEffect(() => {
    let current = true;
    if (open && form.paddleProductId && form.paddleEnvironment) {
      setLoadingPrices(true);
      setPaddlePrices([]);
      listPrices(form.paddleEnvironment, form.paddleProductId)
        .then((prices) => { if (current) setPaddlePrices(prices); })
        .catch((err) => {
          if (!current) return;
          console.error('Failed to load Paddle prices:', err);
          setPaddlePrices([]);
        })
        .finally(() => { if (current) setLoadingPrices(false); });
    } else {
      setPaddlePrices([]);
      setLoadingPrices(false);
    }
    return () => { current = false; };
  }, [open, projectId, form.paddleProductId, form.paddleEnvironment, listPrices]);

  const isLoading = connectionLoading || loadingProducts || loadingPrices;

  const handleChange = useCallback(
    (field: keyof PaddleProductFormState) =>
      (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | { target: { value: unknown } }) => {
        const value = event.target.value;
        setForm((prev) => ({ ...prev, [field]: value }));
      },
    []
  );

  const handleEnvironmentChange = useCallback(
    (event: { target: { value: unknown } }) => {
      const env = event.target.value as 'sandbox' | 'production';
      setForm((prev) => ({
        ...prev,
        paddleEnvironment: env,
        paddleProductId: '', // Reset selections when environment changes
        paddlePriceId: '',
      }));
    },
    []
  );

  const handleProductSelect = useCallback(
    (event: { target: { value: unknown } }) => {
      const productId = event.target.value as string;
      const selectedProduct = paddleProducts.find((p) => p.id === productId);
      setForm((prev) => ({
        ...prev,
        paddleProductId: productId,
        paddlePriceId: '', // Reset price when product changes
        name: prev.name || selectedProduct?.name || '',
        description: prev.description || selectedProduct?.description || '',
      }));
    },
    [paddleProducts]
  );

  const handleSubmit = useCallback(async () => {
    // Validation
    if (!form.name.trim()) {
      setError('Name is required');
      return;
    }
    if (!form.paddleProductId) {
      setError('Please select a Paddle product');
      return;
    }
    if (!form.paddlePriceId) {
      setError('Please select a price');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const data: Omit<PaywallProduct, 'id' | 'createdAt' | 'updatedAt'> = {
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        type: form.type,
        paymentProvider: 'paddle',
        paddleProductId: form.paddleProductId,
        paddlePriceId: form.paddlePriceId,
        paddleEnvironment: form.paddleEnvironment,
        checkoutMode: form.checkoutMode,
      };

      // Add subscription config if subscription type
      if (form.type === 'subscription') {
        data.subscription = {
          interval: form.interval,
          intervalCount: form.intervalCount,
        };
      }

      await onSave(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save product');
    } finally {
      setSaving(false);
    }
  }, [form, onSave]);

  const isEditing = !!product;
  const currentEnvConnected = form.paddleEnvironment === 'sandbox' ? sandboxConnected : productionConnected;

  // Filter prices based on product type
  const filteredPrices = paddlePrices.filter((price) => {
    if (form.type === 'subscription') {
      return price.billingCycle !== undefined;
    } else {
      return price.billingCycle === undefined;
    }
  });

  return (
    <Dialog data-testid={PAYWALL_PRODUCTS_TEST_IDS.paddle.dialog} open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: 'grid',
          gridTemplateColumns: '40px 1fr 40px',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
          <IconButton
            onClick={onClose}
            disabled={saving}
            size="small"
            aria-label="close dialog"
            data-testid={PAYWALL_PRODUCTS_TEST_IDS.paddle.close}
            sx={{ backgroundColor: (theme) => theme.palette.action.hover }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="center"
          sx={{ minWidth: 0, overflow: 'hidden' }}
        >
          <Box
            component="span"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {isEditing ? 'Edit Paddle Product' : 'Create Paddle Product'}
          </Box>
          <Chip
            label="Paddle"
            size="small"
            sx={{ bgcolor: '#FFDE59', color: '#1B1B1B', fontWeight: 600 }}
          />
        </Stack>
        <Box />
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {/* Environment Selection - CRITICAL: This is selected per-product */}
          <FormControl fullWidth>
            <InputLabel>Paddle Environment *</InputLabel>
            <Select
              data-testid={PAYWALL_PRODUCTS_TEST_IDS.paddle.env}
              value={form.paddleEnvironment}
              label="Paddle Environment *"
              onChange={handleEnvironmentChange}
              disabled={isLoading}
            >
              <MenuItem data-testid={PAYWALL_PRODUCTS_TEST_IDS.paddle.envSandbox} value="sandbox" disabled={!sandboxConnected}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <ScienceIcon fontSize="small" color={sandboxConnected ? 'warning' : 'disabled'} />
                  <span>Sandbox</span>
                  {!sandboxConnected && (
                    <Chip label="Not Connected" size="small" sx={{ ml: 1 }} />
                  )}
                </Stack>
              </MenuItem>
              <MenuItem data-testid={PAYWALL_PRODUCTS_TEST_IDS.paddle.envProduction} value="production" disabled={!productionConnected}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <RocketLaunchIcon fontSize="small" color={productionConnected ? 'success' : 'disabled'} />
                  <span>Production</span>
                  {!productionConnected && (
                    <Chip label="Not Connected" size="small" sx={{ ml: 1 }} />
                  )}
                </Stack>
              </MenuItem>
            </Select>
          </FormControl>

          {/* Environment Status Alert */}
          <Alert
            severity={form.paddleEnvironment === 'sandbox' ? 'warning' : 'success'}
            sx={{ mb: 0 }}
          >
            <Typography variant="body2">
              {form.paddleEnvironment === 'sandbox'
                ? '🧪 Sandbox — Using Paddle test products/prices for development'
                : '🚀 Production — Using Paddle live products/prices for real payments'}
            </Typography>
          </Alert>

          {!currentEnvConnected && (
            <Alert severity="info">
              <Typography variant="body2">
                Please connect Paddle {form.paddleEnvironment} in Integrations first.
              </Typography>
            </Alert>
          )}

          <Divider sx={{ my: 1 }} />

          {/* Basic Info */}
          <TextField
            data-testid={PAYWALL_PRODUCTS_TEST_IDS.paddle.name}
            label="Product Name"
            value={form.name}
            onChange={handleChange('name')}
            fullWidth
            required
            helperText="Display name in your app"
            disabled={!currentEnvConnected}
          />

          <TextField
            data-testid={PAYWALL_PRODUCTS_TEST_IDS.paddle.description}
            label="Description"
            value={form.description}
            onChange={handleChange('description')}
            fullWidth
            multiline
            rows={2}
            disabled={!currentEnvConnected}
          />

          {/* Product Type */}
          <FormControl fullWidth disabled={!currentEnvConnected}>
            <InputLabel>Type</InputLabel>
            <Select
              data-testid={PAYWALL_PRODUCTS_TEST_IDS.paddle.type}
              value={form.type}
              label="Type"
              onChange={handleChange('type') as any}
            >
              <MenuItem value="subscription">Subscription</MenuItem>
              <MenuItem value="one_time">One-time Payment</MenuItem>
            </Select>
          </FormControl>

          {/* Checkout Mode Selection */}
          <FormControl fullWidth disabled={!currentEnvConnected}>
            <InputLabel>Checkout Mode</InputLabel>
            <Select
              data-testid={PAYWALL_PRODUCTS_TEST_IDS.paddle.checkoutMode}
              value={form.checkoutMode}
              label="Checkout Mode"
              onChange={handleChange('checkoutMode') as any}
            >
              <MenuItem value="redirect">Paddle Checkout Overlay</MenuItem>
              <MenuItem value="embedded">Embedded Inline Checkout</MenuItem>
            </Select>
          </FormControl>

          <Divider sx={{ my: 1 }} />

          {/* Paddle Product Selection */}
          <Typography variant="subtitle2" color="text.secondary">
            Paddle Product & Price
          </Typography>

          <FormControl fullWidth disabled={!currentEnvConnected || loadingProducts}>
            <InputLabel>Paddle Product</InputLabel>
            <Select
              data-testid={PAYWALL_PRODUCTS_TEST_IDS.paddle.product}
              value={form.paddleProductId}
              label="Paddle Product"
              onChange={handleProductSelect as any}
            >
              {paddleProducts.map((p) => (
                <MenuItem data-testid={PAYWALL_PRODUCTS_TEST_IDS.paddle.productOption(p.id)} key={p.id} value={p.id}>
                  {p.name}
                </MenuItem>
              ))}
            </Select>
            {loadingProducts && (
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                <Typography variant="caption" color="text.secondary">
                  Loading products...
                </Typography>
              </Box>
            )}
            {!loadingProducts && paddleProducts.length === 0 && currentEnvConnected && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                No products found in {form.paddleEnvironment}. Create products in Paddle Dashboard.
              </Typography>
            )}
          </FormControl>

          {/* Price Selection */}
          {form.paddleProductId && (
            <FormControl fullWidth disabled={!currentEnvConnected || loadingPrices}>
              <InputLabel>Price</InputLabel>
              <Select
                data-testid={PAYWALL_PRODUCTS_TEST_IDS.paddle.price}
                value={form.paddlePriceId}
                label="Price"
                onChange={handleChange('paddlePriceId') as any}
              >
                {filteredPrices.map((p) => (
                  <MenuItem data-testid={PAYWALL_PRODUCTS_TEST_IDS.paddle.priceOption(p.id)} key={p.id} value={p.id}>
                    {formatPaddlePriceLabel(p)}
                  </MenuItem>
                ))}
              </Select>
              {loadingPrices && (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  <Typography variant="caption" color="text.secondary">
                    Loading prices...
                  </Typography>
                </Box>
              )}
              {!loadingPrices && filteredPrices.length === 0 && form.paddleProductId && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  No {form.type === 'subscription' ? 'recurring' : 'one-time'} prices found.
                  Create prices in Paddle Dashboard.
                </Typography>
              )}
            </FormControl>
          )}

          {/* Subscription Options */}
          {form.type === 'subscription' && (
            <>
              <Divider sx={{ my: 1 }} />

              <Typography variant="subtitle2" color="text.secondary">
                Subscription Options
              </Typography>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl sx={{ flex: 1 }} disabled={!currentEnvConnected}>
                  <InputLabel>Billing Interval</InputLabel>
                  <Select
                    value={form.interval}
                    label="Billing Interval"
                    onChange={handleChange('interval') as any}
                  >
                    <MenuItem value="day">Daily</MenuItem>
                    <MenuItem value="week">Weekly</MenuItem>
                    <MenuItem value="month">Monthly</MenuItem>
                    <MenuItem value="year">Yearly</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  label="Every X"
                  type="number"
                  value={form.intervalCount}
                  onChange={handleChange('intervalCount')}
                  sx={{ width: 100 }}
                  inputProps={{ min: 1, max: 12 }}
                  disabled={!currentEnvConnected}
                />
              </Box>

              <Alert severity="info" sx={{ mt: 1 }}>
                <Typography variant="body2">
                  💡 Trial periods are configured in Paddle Dashboard at price level.
                </Typography>
              </Alert>
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          variant="contained"
          data-testid={PAYWALL_PRODUCTS_TEST_IDS.paddle.submit}
          onClick={handleSubmit}
          disabled={saving || isLoading || !currentEnvConnected}
          startIcon={saving && <CircularProgress size={16} color="inherit" />}
        >
          {saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Product'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Format Paddle price for display in dropdown
 */
function formatPaddlePriceLabel(price: PaddlePrice): string {
  const amount = formatPaddlePrice(price.unitPrice.amount, price.unitPrice.currencyCode, 'en-US');
  if (!amount) return 'Price unavailable';

  if (price.billingCycle) {
    const { interval, frequency } = price.billingCycle;
    if (frequency === 1) {
      return `${amount}/${interval}`;
    }
    return `${amount} every ${frequency} ${interval}s`;
  }

  return `${amount} (one-time)`;
}

export default PaddlePaywallProductDialog;
